import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Bell, Calendar, Clock, ChevronRight, AlertCircle, 
  PartyPopper, Plane, Briefcase, Coffee, Home, Flag,
  UserPlus, Heart, Sparkles, Cake, Gift, Users,
  Award, Megaphone, CalendarDays, Timer, Trophy, Star, Hand
} from 'lucide-react';
import { Badge } from './ui/badge';
import axios from 'axios';

const BACKEND_URL = '';

// Pass an explicit role to avoid the historical "employee_token || admin_token" priority bug
// (an admin with stale employee_token in localStorage would otherwise send the wrong token).
const createApi = (role) => {
  const api = axios.create({ baseURL: BACKEND_URL, timeout: 30000 });
  api.interceptors.request.use((config) => {
    let token = null;
    if (role === 'admin') {
      token = localStorage.getItem('admin_token');
    } else if (role === 'employee') {
      token = localStorage.getItem('employee_token');
    } else {
      // Legacy fallback (when no role specified): prefer admin if both exist (admin dashboard)
      token = localStorage.getItem('admin_token') || localStorage.getItem('employee_token');
    }
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  return api;
};

const noticeApi = null; // Lazy-init per component instance based on role prop

// Leave type icons and colors
const leaveTypeConfig = {
  pl: { icon: Plane, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Privilege Leave' },
  sl: { icon: Coffee, color: 'text-red-500', bg: 'bg-red-50', label: 'Sick Leave' },
  cl: { icon: Coffee, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Casual Leave' },
  wfh: { icon: Home, color: 'text-pink-500', bg: 'bg-pink-50', label: 'Work From Home' },
  ml: { icon: Flag, color: 'text-indigo-500', bg: 'bg-indigo-50', label: 'Mandatory Holiday' },
  mandatory: { icon: Flag, color: 'text-indigo-500', bg: 'bg-indigo-50', label: 'Public Holiday' },
  ol: { icon: PartyPopper, color: 'text-purple-500', bg: 'bg-purple-50', label: 'Optional Holiday' },
  comp: { icon: Briefcase, color: 'text-cyan-500', bg: 'bg-cyan-50', label: 'Comp Off' },
  default: { icon: Calendar, color: 'text-neutral-500', bg: 'bg-white/40', label: 'Leave' }
};

export default function NoticeBoard({ compact = false, isAdmin = false, role: roleProp }) {
  // Determine the active role: explicit `role` prop > isAdmin shorthand > runtime detection
  const role = useMemo(() => {
    if (roleProp === 'admin' || roleProp === 'employee') return roleProp;
    if (isAdmin) return 'admin';
    if (typeof window !== 'undefined' && localStorage.getItem('admin_token')) return 'admin';
    if (typeof window !== 'undefined' && localStorage.getItem('employee_token')) return 'employee';
    return null;
  }, [roleProp, isAdmin]);
  const noticeApi = useMemo(() => createApi(role), [role]);
  const [fetchError, setFetchError] = useState(false);
  const [combinedSchedule, setCombinedSchedule] = useState([]);
  const [newJoiners, setNewJoiners] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [teamAbsences, setTeamAbsences] = useState({ today: [], tomorrow: [], day_after: [] });
  const [shoutouts, setShoutouts] = useState([]);
  const [companyEvents, setCompanyEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    fetchNoticeBoardData();
    return () => { mountedRef.current = false; };
    // Re-fetch whenever the active API instance changes (e.g., when the
    // explicit `role` prop arrives after first paint on slow mobile JS
    // hydration). Guarantees the call uses the correct admin/employee
    // token instead of a stale memoized one.
  }, [noticeApi]);

  const fetchNoticeBoardData = async () => {
    try {
      setLoading(true);
      setFetchError(false);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sixtyDaysLater = new Date(today);
      sixtyDaysLater.setDate(sixtyDaysLater.getDate() + 60);

      // Determine if we should also fetch personal leaves
      const hasEmployeeToken = !!localStorage.getItem('employee_token');
      const hasAdminToken = !!localStorage.getItem('admin_token');
      const shouldFetchLeaves = hasEmployeeToken && !hasAdminToken;

      // Track failures explicitly (instead of swallowing them silently)
      let allDataFailed = false;
      const [allDataRes, leavesRes] = await Promise.all([
        noticeApi.get('/api/notices/all').catch((err) => {
          allDataFailed = true;
          console.warn('[NoticeBoard] /api/notices/all failed:', err?.response?.status, err?.message);
          return { data: {} };
        }),
        shouldFetchLeaves
          ? noticeApi.get('/api/employee/leaves').catch((err) => {
              console.warn('[NoticeBoard] /api/employee/leaves failed:', err?.response?.status, err?.message);
              return { data: [] };
            })
          : Promise.resolve({ data: [] })
      ]);

      if (!mountedRef.current) return;
      if (allDataFailed) setFetchError(true);

      const allData = allDataRes.data || {};
      const leaves = leavesRes.data || [];

      // Filter upcoming approved personal leaves (next 60 days)
      const upcomingLeaves = leaves
        .filter(leave => {
          if (leave.status !== 'approved') return false;
          const startDate = new Date(leave.start_date);
          return startDate >= today && startDate <= sixtyDaysLater;
        })
        .map(leave => ({
          type: 'personal_leave',
          leave_type: leave.leave_type,
          name: leaveTypeConfig[leave.leave_type?.toLowerCase()]?.label || 'Leave',
          date: leave.start_date,
          end_date: leave.end_date,
          is_half_day: leave.is_half_day,
          half_day_type: leave.half_day_type,
          sortDate: new Date(leave.start_date)
        }));

      // Fetch mandatory holidays from consolidated response
      const mandatoryHolidays = allData.holidays?.mandatory || [];
      const upcomingHolidays = mandatoryHolidays
        .filter(h => {
          const holidayDate = new Date(h.date);
          return holidayDate >= today && holidayDate <= sixtyDaysLater;
        })
        .map(h => ({
          type: 'mandatory',
          leave_type: 'mandatory',
          name: h.name,
          date: h.date,
          day: h.day,
          sortDate: new Date(h.date)
        }));

      // Combine and sort by date
      const combined = [...upcomingLeaves, ...upcomingHolidays]
        .sort((a, b) => a.sortDate - b.sortDate)
        .slice(0, 6);

      if (!mountedRef.current) return;

      setCombinedSchedule(combined);
      setNewJoiners(allData.new_joiners || []);
      setDepartures(allData.departures || []);
      setBirthdays(allData.birthdays || []);
      setAnniversaries(allData.anniversaries || []);
      
      const absData = allData.team_absences || {};
      setTeamAbsences({
        today: absData.today?.absences || [],
        tomorrow: absData.tomorrow?.absences || [],
        day_after: absData.day_after?.absences || []
      });
      
      setShoutouts(allData.shoutouts || []);
      setCompanyEvents(allData.company_events || []);

    } catch (error) {
      console.error('Error fetching notice board data:', error);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short',
      weekday: 'short'
    });
  };

  const getDaysUntil = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateStr);
    targetDate.setHours(0, 0, 0, 0);
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 7) return `In ${diffDays} days`;
    if (diffDays < 14) return 'Next week';
    return `In ${Math.ceil(diffDays / 7)} weeks`;
  };

  const getDaysSince = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateStr);
    targetDate.setHours(0, 0, 0, 0);
    const diffTime = today - targetDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  const getConfig = (item) => {
    if (item.type === 'mandatory') {
      return leaveTypeConfig.mandatory;
    }
    return leaveTypeConfig[item.leave_type?.toLowerCase()] || leaveTypeConfig.default;
  };

  // Helper to format years for anniversary
  const formatYears = (years) => {
    if (years === 1) return '1 year';
    return `${years} years`;
  };

  // Check if there are any team absences
  const hasTeamAbsences = teamAbsences.today.length > 0 || 
                          teamAbsences.tomorrow.length > 0 || 
                          teamAbsences.day_after.length > 0;

  if (loading) {
    return (
      <div className="glass-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-neutral-400" />
          <h3 className="font-semibold text-[#1a1a2e]">Notice Board</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-neutral-100 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-neutral-100 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasContent = combinedSchedule.length > 0 || newJoiners.length > 0 || departures.length > 0 || 
                     birthdays.length > 0 || anniversaries.length > 0 || hasTeamAbsences ||
                     shoutouts.length > 0 || companyEvents.length > 0;

  return (
    <div className="glass-card shadow-soft overflow-hidden" data-testid="notice-board">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-teal-50 to-emerald-50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-teal-100 flex items-center justify-center">
            <Bell className="w-4 h-4 text-teal-600" />
          </div>
          <div>
            <h3 className="font-semibold text-[#1a1a2e]">Notice Board</h3>
            <p className="text-xs text-neutral-500">Updates & announcements</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-5">
        
        {/* Company Events Section - Top Priority */}
        {companyEvents.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays className="w-4 h-4 text-indigo-500" />
              <h4 className="text-sm font-medium text-neutral-700">Upcoming Events</h4>
            </div>
            <div className="space-y-2">
              {companyEvents.map((event, idx) => (
                <div 
                  key={idx}
                  className="p-3 rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white shadow-soft">
                        {event.notice_type === 'town_hall' ? (
                          <Megaphone className="w-5 h-5" />
                        ) : (
                          <CalendarDays className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#1a1a2e]">{event.title}</p>
                        <p className="text-xs text-neutral-600 mt-0.5">{event.description}</p>
                        <p className="text-xs text-indigo-600 mt-1 font-medium">
                          <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(event.event_date)}</span>
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-indigo-100 text-indigo-700 text-xs border-0 shrink-0">
                      {event.days_until === 0 ? 'Today!' : event.days_until === 1 ? 'Tomorrow!' : `${event.days_until} days`}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shoutouts / Recognition Section */}
        {shoutouts.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-4 h-4 text-amber-500" />
              <h4 className="text-sm font-medium text-neutral-700 inline-flex items-center gap-1.5">Recognition <Star className="w-3.5 h-3.5 text-amber-500" /></h4>
            </div>
            <div className="space-y-2">
              {shoutouts.map((shoutout, idx) => {
                const isBadgeRecognition = shoutout.type === 'badge_recognition';
                const hasMultipleEmployees = shoutout.employees && shoutout.employees.length > 1;
                const employeeNames = hasMultipleEmployees 
                  ? shoutout.employees.map(e => e.name).join(', ')
                  : shoutout.employee_name;
                
                // Badge Recognition Card
                if (isBadgeRecognition && shoutout.badge) {
                  const badge = shoutout.badge;
                  return (
                    <div 
                      key={idx}
                      className="p-4 rounded-xl bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 border border-indigo-200/60 relative overflow-hidden shadow-soft"
                      data-testid="badge-recognition-card"
                    >
                      {/* Decorative gradient orbs */}
                      <div className="absolute -top-6 -right-6 w-20 h-20 bg-gradient-to-br from-indigo-200/30 to-purple-200/30 rounded-full blur-xl" />
                      <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-pink-200/30 to-amber-200/30 rounded-full blur-xl" />
                      
                      <div className="relative flex items-start gap-4">
                        {/* Badge Display */}
                        <div 
                          className="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 border-2"
                          style={{ 
                            backgroundColor: badge.color ? `${badge.color}20` : '#EEF2FF',
                            borderColor: badge.color || '#6366F1'
                          }}
                        >
                          {badge.image_url ? (
                            <img 
                              src={badge.image_url} 
                              alt={badge.name} 
                              className="w-10 h-10 rounded-xl object-cover" 
                            />
                          ) : (
                            <Trophy 
                              className="w-7 h-7" 
                              style={{ color: badge.color || '#6366F1' }} 
                            />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Badge name tag */}
                          <div className="flex items-center gap-2 mb-1">
                            <span 
                              className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                              style={{ 
                                backgroundColor: badge.color ? `${badge.color}15` : '#EEF2FF',
                                color: badge.color || '#4F46E5'
                              }}
                            >
                              <Award className="w-3 h-3" />
                              {badge.name || 'Achievement Badge'}
                            </span>
                          </div>
                          
                          {/* Recipients */}
                          <p className="text-sm font-bold text-[#1a1a2e]">
                            {employeeNames}
                          </p>
                          
                          {/* Employee count for multiple */}
                          {hasMultipleEmployees && (
                            <p className="text-xs text-neutral-500 mt-0.5">
                              {shoutout.employees.length} team members earned this badge
                            </p>
                          )}
                          
                          {/* Message */}
                          <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
                            {shoutout.message}
                          </p>
                          
                          {/* Footer */}
                          <p className="text-xs text-neutral-400 mt-2 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            Awarded by {shoutout.created_by_name}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Regular Shoutout Card (unchanged)
                return (
                  <div 
                    key={idx}
                    className="p-3 rounded-xl bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border border-amber-200 relative overflow-hidden"
                  >
                    {/* Confetti-like decorative elements */}
                    <PartyPopper className="absolute top-2 right-2 w-6 h-6 text-amber-400 opacity-30" />
                    <Star className="absolute bottom-2 left-2 w-5 h-5 text-amber-400 opacity-30" />
                    
                    <div className="flex items-start gap-3 relative">
                      {/* Multiple avatars for team recognition */}
                      {hasMultipleEmployees ? (
                        <div className="flex -space-x-2 shrink-0">
                          {shoutout.employees.slice(0, 3).map((emp, empIdx) => (
                            <div 
                              key={empIdx}
                              className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-400 flex items-center justify-center text-white font-bold text-xs shadow-soft ring-2 ring-white"
                              title={emp.name}
                            >
                              {emp.name?.charAt(0) || '?'}
                            </div>
                          ))}
                          {shoutout.employees.length > 3 && (
                            <div className="w-9 h-9 rounded-full bg-amber-300 flex items-center justify-center text-amber-800 font-bold text-xs shadow-soft ring-2 ring-white">
                              +{shoutout.employees.length - 3}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-400 flex items-center justify-center text-white font-bold text-sm shadow-soft ring-2 ring-amber-200">
                          {shoutout.employee_name?.charAt(0) || '?'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#1a1a2e]">
                          <span className="inline-flex items-center gap-1"><Trophy className="w-3 h-3 text-amber-600" /> {employeeNames}</span>
                        </p>
                        <p className="text-xs text-neutral-500">
                          {hasMultipleEmployees 
                            ? `${shoutout.employees.length} team members recognized`
                            : `${shoutout.employee_designation} • ${shoutout.employee_department}`
                          }
                        </p>
                        <p className="text-sm text-amber-800 mt-2 italic leading-relaxed">
                          &quot;{shoutout.message}&quot;
                        </p>
                        <p className="text-xs text-neutral-400 mt-2">
                          — {shoutout.created_by_name}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Team Absences Section */}
        {hasTeamAbsences && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-neutral-500" />
              <h4 className="text-sm font-medium text-neutral-700">Team Absences</h4>
            </div>
            <div className="space-y-2">
              {/* Today's absences */}
              {teamAbsences.today.length > 0 && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-100">
                  <p className="text-xs font-semibold text-red-700 mb-2">Today</p>
                  <div className="space-y-1.5">
                    {teamAbsences.today.map((absence, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-xs font-semibold">
                          {absence.name?.charAt(0)}
                        </div>
                        <span className="text-neutral-700">{absence.name}</span>
                        <Badge variant="outline" className="text-xs border-red-200 text-red-600">
                          {leaveTypeConfig[absence.leave_type?.toLowerCase()]?.label || absence.leave_type}
                          {absence.is_half_day && ' (½)'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Tomorrow's absences */}
              {teamAbsences.tomorrow.length > 0 && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                  <p className="text-xs font-semibold text-amber-700 mb-2">Tomorrow</p>
                  <div className="space-y-1.5">
                    {teamAbsences.tomorrow.map((absence, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 text-xs font-semibold">
                          {absence.name?.charAt(0)}
                        </div>
                        <span className="text-neutral-700">{absence.name}</span>
                        <Badge variant="outline" className="text-xs border-amber-200 text-amber-600">
                          {leaveTypeConfig[absence.leave_type?.toLowerCase()]?.label || absence.leave_type}
                          {absence.is_half_day && ' (½)'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Day after tomorrow's absences */}
              {teamAbsences.day_after.length > 0 && (
                <div className="p-3 rounded-xl bg-white/40 border border-white/40">
                  <p className="text-xs font-semibold text-neutral-600 mb-2">Day After Tomorrow</p>
                  <div className="space-y-1.5">
                    {teamAbsences.day_after.map((absence, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <div className="w-6 h-6 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 text-xs font-semibold">
                          {absence.name?.charAt(0)}
                        </div>
                        <span className="text-neutral-700">{absence.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {leaveTypeConfig[absence.leave_type?.toLowerCase()]?.label || absence.leave_type}
                          {absence.is_half_day && ' (½)'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* New Joiners Section */}
        {newJoiners.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <h4 className="text-sm font-medium text-neutral-700 inline-flex items-center gap-1.5">Welcome to the Team! <PartyPopper className="w-3.5 h-3.5 text-amber-500" /></h4>
            </div>
            <div className="space-y-2">
              {newJoiners.map((joiner, idx) => (
                <div 
                  key={idx}
                  className="p-3 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-400 flex items-center justify-center text-white font-bold text-sm shadow-soft">
                      {joiner.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1a1a2e]">{joiner.name}</p>
                      <p className="text-xs text-neutral-500">{joiner.designation} • {joiner.department}</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 text-xs border-0">
                      {getDaysSince(joiner.first_working_day)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Departures / Farewell Section */}
        {departures.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Heart className="w-4 h-4 text-pink-500" />
              <h4 className="text-sm font-medium text-neutral-700 inline-flex items-center gap-1.5">Wishing Farewell <Hand className="w-3.5 h-3.5 text-blue-500" /></h4>
            </div>
            <div className="space-y-2">
              {departures.map((person, idx) => (
                <div 
                  key={idx}
                  className="p-3 rounded-xl bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-100"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-400 flex items-center justify-center text-white font-bold text-sm shadow-soft">
                      {person.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1a1a2e]">{person.name}</p>
                      <p className="text-xs text-neutral-500">{person.designation} • Last day: {formatDate(person.last_working_day)}</p>
                      <p className="text-xs text-pink-600 mt-1 italic">&quot;{person.farewell_message}&quot;</p>
                    </div>
                    <Badge className="bg-pink-100 text-pink-700 text-xs border-0 shrink-0">
                      {getDaysUntil(person.last_working_day)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Combined Upcoming Leaves & Holidays Section */}
        {combinedSchedule.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-neutral-400" />
              <h4 className="text-sm font-medium text-neutral-700">Upcoming Schedule</h4>
              <span className="text-xs text-neutral-400">Next 60 days</span>
            </div>
            <div className="space-y-2">
              {combinedSchedule.map((item, idx) => {
                const config = getConfig(item);
                const Icon = config.icon;
                const isMandatoryHoliday = item.type === 'mandatory';
                
                return (
                  <div 
                    key={idx}
                    className={`p-3 rounded-xl ${config.bg} border border-slate-100 transition-all hover:shadow-soft`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-soft">
                          <Icon className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1a1a2e]">
                            {item.name}
                          </p>
                          <p className="text-xs text-neutral-500 mt-0.5">
                            {formatDate(item.date)}
                            {item.end_date && item.date !== item.end_date && ` - ${formatDate(item.end_date)}`}
                            {isMandatoryHoliday && item.day && ` • ${item.day}`}
                          </p>
                          {item.is_half_day && (
                            <Badge variant="outline" className="text-xs mt-1 border-white/40">
                              Half Day ({item.half_day_type === 'first_half' ? 'Morning' : 'Afternoon'})
                            </Badge>
                          )}
                          {isMandatoryHoliday && (
                            <Badge className="text-xs mt-1 bg-indigo-100 text-indigo-600 border-0">
                              Public Holiday
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Badge className="bg-neutral-100 text-neutral-600 text-xs font-normal">
                        {getDaysUntil(item.date)}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Birthday Wishes Section */}
        {birthdays.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-4 h-4 text-pink-500" />
              <h4 className="text-sm font-medium text-neutral-700 inline-flex items-center gap-1.5">Happy Birthday! <Cake className="w-3.5 h-3.5 text-pink-500" /></h4>
            </div>
            <div className="space-y-2">
              {birthdays.map((person, idx) => (
                <div 
                  key={idx}
                  className="p-3 rounded-xl bg-gradient-to-r from-pink-50 via-rose-50 to-orange-50 border border-pink-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 via-rose-400 to-orange-400 flex items-center justify-center text-white shadow-soft animate-pulse">
                      <Cake className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1a1a2e]">{person.name}</p>
                      <p className="text-xs text-neutral-500">{person.designation} • {person.department}</p>
                    </div>
                    <Badge className="bg-pink-100 text-pink-700 text-xs border-0 shrink-0 inline-flex items-center gap-1">
                      <PartyPopper className="w-3 h-3" /> Today!
                    </Badge>
                  </div>
                  <p className="text-xs text-pink-600 mt-2 text-center italic">
                    Wishing you a fantastic birthday!
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Work Anniversaries Section - Bottom Priority */}
        {anniversaries.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Cake className="w-4 h-4 text-purple-500" />
              <h4 className="text-sm font-medium text-neutral-700 inline-flex items-center gap-1.5">Work Anniversaries <Cake className="w-3.5 h-3.5 text-emerald-500" /></h4>
            </div>
            <div className="space-y-2">
              {anniversaries.map((person, idx) => (
                <div 
                  key={idx}
                  className="p-3 rounded-xl bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-violet-500 flex items-center justify-center text-white font-bold text-sm shadow-soft">
                      {person.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1a1a2e]">
                        {person.name}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {person.designation} • {person.department}
                      </p>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700 text-xs border-0 shrink-0 inline-flex items-center gap-1">
                      <PartyPopper className="w-3 h-3" /> {formatYears(person.years)}
                    </Badge>
                  </div>
                  <p className="text-xs text-purple-600 mt-2 text-center italic">
                    Celebrating {formatYears(person.years)} of amazing work!
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State — distinguishes "all clear" vs "couldn't load" */}
        {!hasContent && (
          <div className="text-center py-6" data-testid="notice-board-empty">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${fetchError ? 'bg-amber-50' : 'bg-emerald-50'}`}>
              {fetchError ? (
                <AlertCircle className="w-6 h-6 text-amber-500" />
              ) : (
                <Sparkles className="w-6 h-6 text-emerald-500" />
              )}
            </div>
            {fetchError ? (
              <>
                <p className="text-sm font-medium text-amber-700">Couldn&apos;t load notice board</p>
                <p className="text-xs text-neutral-500 mt-1">Please refresh — if this persists, sign out and back in.</p>
                <button
                  onClick={fetchNoticeBoardData}
                  className="mt-3 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                  data-testid="notice-board-retry"
                >
                  Retry
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-[#1a1a2e]">All clear on the horizon</p>
                <p className="text-xs text-neutral-500 mt-1">No upcoming events, birthdays, or holidays in the next 60 days.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
